import { ref, onMounted, onUnmounted } from 'vue'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function usePWAInstall() {
  const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)
  const showInstallPrompt = ref(false)
  const isInstalled = ref(false)

  const handleBeforeInstallPrompt = (e: Event) => {
    e.preventDefault()
    deferredPrompt.value = e as BeforeInstallPromptEvent
    showInstallPrompt.value = true
  }

  const handleAppInstalled = () => {
    deferredPrompt.value = null
    showInstallPrompt.value = false
    isInstalled.value = true
    console.log('PWA was installed')
  }

  const installPWA = async () => {
    if (!deferredPrompt.value) {
      return
    }

    deferredPrompt.value.prompt()
    const { outcome } = await deferredPrompt.value.userChoice

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt')
    }

    deferredPrompt.value = null
    showInstallPrompt.value = false
  }

  const dismissInstallPrompt = () => {
    showInstallPrompt.value = false
    localStorage.setItem('pwa-install-dismissed', Date.now().toString())
  }

  const checkIfDismissedRecently = () => {
    const dismissed = localStorage.getItem('pwa-install-dismissed')
    if (dismissed) {
      const dismissedTime = parseInt(dismissed)
      const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24)
      return daysSinceDismissed < 7
    }
    return false
  }

  const isStandalone = () => {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true
    )
  }

  onMounted(() => {
    if (isStandalone()) {
      isInstalled.value = true
      return
    }

    if (checkIfDismissedRecently()) {
      return
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)
  })

  onUnmounted(() => {
    window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.removeEventListener('appinstalled', handleAppInstalled)
  })

  return {
    showInstallPrompt,
    isInstalled,
    installPWA,
    dismissInstallPrompt
  }
}
